import { datadogRum } from '@datadog/browser-rum';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import {
  ApiError,
  ApplicantPortalService,
  DataFields,
} from 'api-clients/monolith';
import cx from 'classnames';
import _ from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import { AppSetup } from 'components/AppSetup';
import { CompletedContent } from 'components/CompletedContent';
import { ConversionTracking } from 'components/ConversionTracking';
import { Snackbar } from 'components/Snackbar';
import { useLoginExperiment } from 'features/ContextsProvider/useLoginExperiment';
import { LoginSelection } from 'features/LoginSelection/LoginSelection';
import { getLogoUrl } from 'utils/brandLogoUrl';

import { CancelablePromise } from '../../../../api-clients/monolith/core/CancelablePromise';
import { useApiService } from '../../hooks/useApiService';
import { AppFormConfig } from '.';
import { ApplicationFormBodyRoute } from './ApplicationFormBodyRoute';
import { ApplicationFormEmbeddedLayout } from './ApplicationFormEmbeddedLayout';
import { ApplicationFormLanguageBanner } from './ApplicationFormLanguageBanner';
import { ApplicationFormLanguageSelector } from './ApplicationFormLanguageSelector';
import { ApplicationFormLayout } from './ApplicationFormLayout';
import { ReachApplicationFormLanguageSelector } from './ReachApplicationFormLanguageSelector';
import { ReachApplicationFormLayout } from './ReachApplicationFormLayout';

export interface ApplicationFormProps {
  accountSlug: string;
  funnelId: string;
}

interface AppFormConfigWithSlug extends AppFormConfig {
  account: AppFormConfig['account'] & { slug: string };
}

interface Redirect {
  location: string;
}

const useStyles = makeStyles<Theme, { translateAnimationDuration: number }>(
  () =>
    createStyles({
      animateUpdateLanguage: {
        '& span, h1, h2, h3, p': {
          animationName: '$fade',
          animationDuration: props => `${props.translateAnimationDuration}ms`,
        },
      },

      '@keyframes fade': {
        from: {
          opacity: 0,
        },
        to: {
          opacity: 100,
        },
      },
    }),
);

const ApplicationFormTitle: React.VFC<{ brand: string }> = ({
  brand,
}): JSX.Element => {
  const intl = useIntl();
  return (
    <Helmet>
      <title>
        {intl.formatMessage(
          {
            defaultMessage: 'Apply to {brand} (Fountain)',
            description:
              'Page title for the job application form indicating the brand/company being applied to.',
          },
          { brand },
        )}
      </title>
    </Helmet>
  );
};

export const ApplicationForm: FC<ApplicationFormProps> = ({
  accountSlug,
  funnelId,
}) => {
  /**
   * If `getInternalApiPortalApplicationFormsNew` returned the `slug`, the
   * result would align with `getInternalApiPortalApplicationsContexts`
   *
   * @see https://fountain.atlassian.net/browse/AX-987
   */
  const formService = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const reachUtmSource = /^reach.*/.test(params.get('utm_source') ?? '');
    const reachPersonaId = params.get('persona_id');
    const personaId =
      reachUtmSource && reachPersonaId ? reachPersonaId : undefined;
    const promise =
      ApplicantPortalService.getInternalApiPortalApplicationFormsNew(
        accountSlug,
        funnelId,
        params.get('try_new_ui') ?? undefined,
        personaId,
      );
    return new CancelablePromise<AppFormConfigWithSlug>((resolve, reject) => {
      promise
        .then(config =>
          resolve({
            ...config,
            account: { ...config.account, slug: accountSlug },
          }),
        )
        .catch(err => reject(err));
    });
  }, [accountSlug, funnelId]);

  const { result } = useApiService<AppFormConfigWithSlug, Redirect>(
    formService,
  );

  const { path } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();

  const [launchPortalUrl, setLaunchPortalUrl] = useState('');
  const [displayLocale, setDisplayLocale] = useState<string | null>(null);
  const [isUpdatingLocale, setIsUpdatingLocale] = useState(false);
  const [errorToastMessage, setErrorToastMessage] =
    useState<React.ReactNode>(null);

  const TRANSLATE_ANIMATION_DURATION = 300;

  const [loginVariant, handleLogin] = useLoginExperiment(
    result.status === 'ready' ? result.data.config.experiments : [],
  );

  const [showLoginToast, setShowLoginToast] = useState(false);

  const isIframe = window.location !== window.parent.location;

  const combinedFields = useMemo(() => {
    const createUtmInputs = (): DataFields => {
      const params = new URLSearchParams(location.search);
      const hiddenInputs: DataFields = [];
      // the ID field is ignored for hidden inputs so this is expected to be safe
      let fakeId = 1;
      params.forEach((value, key) => {
        hiddenInputs.push({
          type: 'hidden_field',
          id: fakeId,
          key,
          question: `utm parameter ${key}`,
          predefined: false,
          required: false,
          extra: {
            hidden_value: value,
          },
        });
        fakeId += 1;
      });
      return hiddenInputs;
    };
    if (result.status === 'ready') {
      return result.data.funnel.custom_fields.concat(createUtmInputs());
    }
    return [];
  }, [result, location.search]);

  const isReachApplicationFormEnabled = useMemo(() => {
    if (result.status === 'ready') {
      const params = new URLSearchParams(location.search);
      const utmSource = params.get('utm_source') ?? undefined;
      return (
        !!result.data.config.reach_application_form_enabled &&
        /^reach.*/.test(utmSource ?? '') // Match all utm sources that starts with "reach"
      );
    }

    return false;
  }, [location.search, result]);

  useEffect(() => {
    if (result.status === 'ready') {
      result.data.funnel.display_locale = result.data.funnel.locale;
      setDisplayLocale(result.data.funnel.locale);
    }
  }, [result, accountSlug, loginVariant]);

  const launchPortal = () => {
    history.push(launchPortalUrl);
  };
  /* istanbul ignore next -- this code enables Polyglot, which is not GA. If bugs surface, remove this and add a test case to catch regressions */
  async function translateFunnelAndDataFields(targetLocale: string): Promise<
    [
      {
        // eslint-disable-next-line camelcase
        position_description_html?: string;
        translated?: boolean;
      },
      DataFields[],
    ]
  > {
    const params = new URLSearchParams(location.search);
    const personaId = params.get('persona_id') ?? undefined;
    const translationFunnel =
      ApplicantPortalService.postInternalApiPortalFunnelsTranslation(
        accountSlug,
        funnelId,
        {
          to: targetLocale,
          persona_id: personaId,
        },
      );
    const translationDataFields =
      ApplicantPortalService.postInternalApiPortalFunnelsTranslationDataFields(
        accountSlug,
        funnelId,
        {
          to: targetLocale,
          field_ids: combinedFields
            .filter(field => field.type !== 'hidden_field')
            .map(field => field.id),
        },
      );

    const translations = await Promise.all([
      translationFunnel,
      translationDataFields,
    ]);

    if (result.status === 'ready') {
      result.data.funnel = _.merge(result.data.funnel, translations[0]);

      result.data.funnel.custom_fields = _.merge(
        result.data.funnel.custom_fields,
        translations[1],
      );
    }

    return translations;
  }

  const updateLanguage = async (targetLocale: string) => {
    const TRANSLATION_FAILED = (
      <FormattedMessage
        defaultMessage="The translation has failed. Some part of the page may still be untranslated."
        description="Indicates that there was an error during the translation of the page"
      />
    );

    setIsUpdatingLocale(true);
    /* istanbul ignore next -- this code enables Polyglot, which is not GA. If bugs surface, remove this and add a test case to catch regressions */
    if (result.status === 'ready') {
      try {
        await translateFunnelAndDataFields(targetLocale);
        result.data.funnel.display_locale = targetLocale;
        setDisplayLocale(targetLocale);
      } catch (error) {
        if (error instanceof ApiError) {
          setErrorToastMessage(TRANSLATION_FAILED);
        }
        datadogRum.addError(error);
      } finally {
        // Wait for animation to end before removing the animation class
        setTimeout(() => {
          setIsUpdatingLocale(false);
        }, TRANSLATE_ANIMATION_DURATION);
      }
    }
  };

  const styles = useStyles({
    translateAnimationDuration: TRANSLATE_ANIMATION_DURATION,
  });

  if (result.status === 'error' && result.error?.location) {
    window.location.href = result.error.location;
    return null;
  }

  const selectApplicationFormLayourWrapper = () => {
    if (isIframe && !isReachApplicationFormEnabled)
      return ApplicationFormEmbeddedLayout;
    if (isReachApplicationFormEnabled) return ReachApplicationFormLayout;
    return ApplicationFormLayout;
  };

  const ApplicationFormLayoutWrapper = selectApplicationFormLayourWrapper();

  const LanguageSelector = isReachApplicationFormEnabled
    ? ReachApplicationFormLanguageSelector
    : ApplicationFormLanguageSelector;

  if (result.status === 'ready' && loginVariant) {
    return (
      <AppSetup {...result}>
        <LoginSelection
          variant={loginVariant}
          onLogin={() => {
            setShowLoginToast(true);
            handleLogin();
          }}
          logoUrl={getLogoUrl(result.data.brand.logos.image_url)}
          appFormData={result.data}
        />
      </AppSetup>
    );
  }

  return (
    <AppSetup {...result}>
      {result.status === 'ready' && (
        <>
          <Snackbar
            severity="success"
            open={showLoginToast}
            message={
              <FormattedMessage
                defaultMessage="Verification not required for this application"
                description="Temporary message explaining this job application does not require a verification step"
              />
            }
            onClose={() => setShowLoginToast(false)}
          />
          <Snackbar
            severity="error"
            open={errorToastMessage !== null}
            message={errorToastMessage}
            onClose={() => setErrorToastMessage(null)}
          />
          {result.data.config.conversion_trackers.map(tracker => (
            <ConversionTracking key={tracker.provider} tracker={tracker} />
          ))}
          <Helmet>
            <meta
              name="title"
              property="og:title"
              content={result.data.funnel.title}
            />
            <meta
              name="description"
              property="og:description"
              content={result.data.funnel.position_description_html}
            />
            <meta
              name="image"
              property="og:image"
              content={result.data.brand.logos.image_url ?? ''}
            />
            <meta property="og:type" content="website" />
            <meta property="og:locale" content={result.data.funnel.locale} />
            {!result.data.funnel.active && (
              <meta name="robots" content="noindex"></meta>
            )}
          </Helmet>
          <ApplicationFormTitle brand={result.data.brand.name} />
          <ApplicationFormLayoutWrapper
            brandLogo={result.data.brand.logos.image_url ?? null}
            squareLogo={result.data.brand.logos.square_image_url ?? null}
            brandName={result.data.brand.name}
            isDspBrand={result.data.brand.is_amazon_dsp}
            helpEmail={result.data.funnel.company_support_email}
            hideBackButton={
              result.data.funnel.hide_back_to_positions_link ||
              (result.data.funnel.jobs_directory_disabled ?? false)
            }
            accountSlug={accountSlug}
            amazonAccount={result.data.account.is_amazon_account}
            upsAccount={result.data.account.is_ups_account}
            coverImage={result.data.funnel.cover_image}
            directoryPath={result.data.account.directory_path}
            languageSelector={
              displayLocale && (
                <LanguageSelector
                  displayLocale={displayLocale}
                  funnelLocale={result.data.funnel.locale}
                  availableLocales={
                    result.data.funnel.alternative_locales ?? {}
                  }
                  onLocaleChange={locale => void updateLanguage(locale)}
                  loading={isUpdatingLocale}
                />
              )
            }
            languageBanner={
              displayLocale && (
                <ApplicationFormLanguageBanner
                  displayLocale={displayLocale}
                  funnelLocale={result.data.funnel.locale}
                  availableLocales={
                    result.data.funnel.alternative_locales ?? {}
                  }
                  onTranslate={locale => void updateLanguage(locale)}
                />
              )
            }
          >
            <Switch>
              <Route exact path={path}>
                <ApplicationFormBodyRoute
                  className={cx({
                    [styles.animateUpdateLanguage]: isUpdatingLocale,
                  })}
                  accountSlug={accountSlug}
                  opening={{
                    title: isReachApplicationFormEnabled
                      ? result.data.funnel.position_name
                      : result.data.funnel.application_form_title,
                    address: result.data.funnel.address,
                    payRate: result.data.funnel.compensation,
                    countryCode: result.data.funnel.country_code,
                    minWeeklyHours: result.data.funnel.shift?.min_weekly_hours,
                    maxWeeklyHours: result.data.funnel.shift?.max_weekly_hours,
                    brandName: result.data.brand.name,
                    jobHours: result.data.funnel.job_hours,
                    jobType: result.data.funnel.job_type,
                    industry: result.data.funnel.industry,
                  }}
                  jobDescriptionHTML={
                    result.data.funnel.position_description_html
                  }
                  funnelId={funnelId}
                  viewAllJobs={!result.data.funnel.hide_back_to_positions_link}
                  hideJobWording={result.data.funnel.hide_job_wording}
                  dataFields={combinedFields}
                  uploadFields={result.data.funnel.file_upload}
                  setLaunchPortal={url => setLaunchPortalUrl(url)}
                  customSuccessMessage={
                    result.data.funnel.position_custom_success_message
                  }
                  amazonAccount={result.data.account.is_amazon_account}
                  isDspBrand={result.data.brand.is_amazon_dsp}
                  displayLocale={displayLocale ?? result.data.funnel.locale}
                  smsConsentVariant={result.data.funnel.sms_consent_variant}
                  isIframe={isIframe}
                  turnstileEnabled={result.data.config.turnstile_enabled}
                  validateEmail={result.data.config.validate_email}
                  hideEmailField={result.data.config.hide_email_field}
                  isItcDemoEnabled={result.data.config.itc_demo_enabled}
                  requirements={result.data.funnel.job_at_a_glance}
                  reachApplicationFormEnabled={isReachApplicationFormEnabled}
                  isExpandJobDescription={
                    !!result.data.funnel.expand_job_description
                  }
                  upsAccount={result.data.account.is_ups_account}
                  showFullNameField={result.data.config.show_full_name_field}
                  showCompensation={result.data.config.show_compensation}
                  locationRequestEnabled={
                    result.data.config.location_request_enabled
                  }
                />
              </Route>

              <Route path={`${path}/complete`}>
                <CompletedContent
                  customMessage={
                    result.data.funnel.position_custom_success_message
                  }
                  handleClick={launchPortalUrl ? launchPortal : undefined}
                />
              </Route>

              <Route path={`${path}/linkSent`}>
                <CompletedContent
                  headingText={
                    <FormattedMessage
                      defaultMessage="Link sent"
                      description="Indicates link was sent"
                    />
                  }
                  bodyText={
                    <FormattedMessage
                      defaultMessage="Check your email or text messages for a link to pick up your application where you left off."
                      description="Instructions on next steps"
                    />
                  }
                />
              </Route>
            </Switch>
          </ApplicationFormLayoutWrapper>
        </>
      )}
    </AppSetup>
  );
};

export default ApplicationForm;
